// modules
import {
  assign,
  cloneDeep,
  every,
  findIndex,
  findKey,
  flow,
  forEach,
  get,
  has,
  isArray,
  isEmpty,
  omit,
  reduce,
  some
} from 'lodash'
import moment from 'moment'

// constants
import {
  ERRORS_TITLE_LIST,
  FIELD_INDEX,
  FREQUENCY_TYPE_LIST,
  TIME_FORMAT_FOR_SERVER,
  UUID_REGEXP
} from 'src/constants'
// utils
import { moveItem } from 'src/utils/Array'
import { splitCamelCase } from 'src/utils/String'

export const mapDataToLinear = (data, parentUuid = null) => {
  let linearData = []

  data.forEach((item) => {
    if (item.children) {
      item.isParent = true
      linearData = linearData.concat(
        mapDataToLinear(item.children, item.key || null)
      )
    }
    item.parentUuid = parentUuid
    linearData.push(item)
  })

  return linearData
}

export const normalizeAll = (fields) => (values, prevValues) => {
  const mappedFields = mapDataToLinear(fields)
  const isParentNode = !some(mappedFields, 'isParent')
  const parentNodes = mappedFields.filter(({ key }) => values.includes(key))

  if (!values.length) {
    return [undefined]
  }

  if (typeof values[0] === 'undefined') {
    return [undefined]
  }

  if (typeof prevValues[0] === 'undefined') {
    if (every(parentNodes, 'isParent')) {
      return mappedFields
        .filter((field) => field.isParent && !values.includes(field.key))
        .map((item) => item.key)
    }

    if (isParentNode && !prevValues.length) {
      return mappedFields
        .map((field) => field.key)
        .filter((id) => values.includes(id))
    }

    if (isParentNode) {
      return mappedFields
        .map((field) => field.key)
        .filter((id) => !values.includes(id))
    }
    const notParentFiltered = mappedFields.filter(
      (field) => !field.isParent && !values.includes(field.key)
    )
    const resArr = []

    notParentFiltered.forEach((field) => {
      const count = notParentFiltered.filter(
        (item) => item.parentUuid === field.parentUuid
      )

      if (count.length === 1) {
        resArr.push(field)
      }
    })
    return resArr.map((field) => field.key)
  }

  return values
}

export const mapDataHierarchy = (
  hierarchy = [],
  childrenKey = 'children',
  checkDisabled = false,
  showOnlyActive = false
) => {
  if (isEmpty(hierarchy)) {
    return []
  }

  const updatedHierarchy = !Array.isArray(hierarchy)
    ? Object.keys(hierarchy).map((key) => hierarchy[key])
    : hierarchy

  return updatedHierarchy
    .filter(({ isActive, uuid }) => (showOnlyActive ? isActive : true))
    .map((node) => {
      const updatedNode = {
        ...node,
        title: node.name || node.fullName || node.refValue || node.title,
        label: node.name || node.fullName || node.refValue || node.title,
        value: node.uuid || node.key,
        key: node.uuid
      }

      if (childrenKey in node && node.isActive) {
        if (!isEmpty(node[childrenKey])) {
          updatedNode.children = mapDataHierarchy(
            node[childrenKey],
            childrenKey,
            checkDisabled,
            showOnlyActive
          )
        }
      }

      if ('children' in updatedNode && !updatedNode?.children?.length) {
        delete updatedNode.children
      }

      if ('disabled' in node) {
        updatedNode.disabled = node.disabled
      } else if (checkDisabled) {
        const remainingChildren = updatedNode.children
        const originalChildren = node[childrenKey]
        const childrenToCheck = showOnlyActive
          ? remainingChildren
          : originalChildren

        updatedNode.disabled = childrenKey in node && !!childrenToCheck?.length
      } else {
        updatedNode.disabled = false
      }

      return updatedNode
    })
}

export const mapAchievementsHierarchy = (
  hierarchy = [],
  childrenKey = 'children'
) =>
  hierarchy.map((node) => {
    const updatedNode = {
      ...node,
      key: node.uuid
    }

    if (childrenKey in node) {
      if (!isEmpty(node[childrenKey])) {
        updatedNode.children = mapAchievementsHierarchy(node[childrenKey])
        updatedNode.isEditDisable = true
      } else {
        delete updatedNode[childrenKey]
      }
    }

    return updatedNode
  })

export const updateAchievementsHierarchy = (list = [], achievement = {}) => {
  if (isEmpty(list)) return list

  return list.map((item) => {
    if (item.uuid === achievement.uuid) return achievement

    if (!isEmpty(item.children)) {
      item.children = updateAchievementsHierarchy(item.children, achievement)
    }

    return item
  })
}

export const collectErrorMessages = ({
  errorObj = {},
  messages = [],
  dataType = ''
}) => {
  if (!isEmpty(errorObj)) {
    let updMessages = [...messages]

    forEach(errorObj, (item) => {
      if (has(item, 'field') && has(item, 'message')) {
        const title = item.field
          .replace(/(\.|)uuid/i, '')
          .replace(/disease/i, 'problem')
          .split('.')
          .filter((item) => !item.match(UUID_REGEXP))
          .filter((__, key, array) => [0, array.length - 1].indexOf(key) !== -1)
          .map((item) => {
            let title = item.replace(FIELD_INDEX, '')
            title = has(ERRORS_TITLE_LIST, `${dataType}.${title}`)
              ? ERRORS_TITLE_LIST[dataType][title]
              : splitCamelCase(title)

            return title
          })
          .join(' ')
        const index = findIndex(updMessages, ['title', title])

        if (index === -1) {
          updMessages = [...updMessages, { title, messages: [item.message] }]
        } else if (updMessages[index].messages.indexOf(item.message) === -1) {
          updMessages[index].messages.push(item.message)
        }
      } else {
        updMessages = collectErrorMessages({
          errorObj: item,
          messages: updMessages,
          dataType
        })
      }
    })

    return updMessages
  }

  return messages
}

export const parseErrorObj = (err = {}, dataType = '') =>
  flow(cloneDeep, (error) =>
    reduce(
      error,
      (result, item) => {
        const errors = collectErrorMessages({ errorObj: item, dataType })

        result = [...result, ...errors]

        return result
      },
      []
    )
  )(err)

export const convertDataFields = (
  collection = [],
  childrenKey = 'children',
  fields = []
) =>
  Array.isArray(collection)
    ? cloneDeep(collection).map((node, key) => {
        const updatedNode = {
          title: node.title || node.name || node.fullName || node.refValue,
          label: node.title || node.name || node.fullName || node.refValue,
          value: get(node, ['uuid'], get(node, ['value'], null)),
          key: get(node, ['uuid'], get(node, ['key'], key)),
          searchField: get(node, ['searchField'], null)
        }

        if (has(node, 'disabled')) {
          updatedNode.disabled = node.disabled
        }

        fields.forEach((field) => {
          if (field in node) {
            updatedNode[field] = node[field]
          }
        })

        if (childrenKey in node && childrenKey.length) {
          updatedNode[childrenKey] = convertDataFields(
            node[childrenKey],
            childrenKey,
            fields
          )
        }
        return updatedNode
      })
    : collection

export const addAllValue = (
  list = [],
  convertList = false,
  title = 'All',
  value = null
) => [
  {
    key: 0,
    title,
    label: title,
    value,
    children: (convertList ? convertDataFields(list) : list).map((item) => ({
      ...item,
      label: item.label || item.title
    }))
  }
]

export const addSelectAllValue = (list = [], title = 'All', value = null) => [
  {
    key: 0,
    title,
    label: title,
    value
  },
  ...list
]

export const addSelectValue = (
  list = [],
  title = 'All',
  key = 0,
  value = null
) => [
  ...list,
  {
    key,
    title,
    value
  }
]

export const convertFrequencyList = (frequency = {}, isClinicalPathway) => {
  const type = get(
    frequency,
    ['days', 'type'],
    FREQUENCY_TYPE_LIST.get(0).value
  )
  const reminders = get(frequency, ['reminders'], [])
  const updFrequency = {
    ...cloneDeep(frequency),
    reminders: flow(
      (items) => (isArray(items) ? items : []),
      (items) =>
        items.map(({ days, minutes }) => ({
          days: +days,
          minutes: +minutes,
          isActive: +days !== 0 || +minutes !== 0
        }))
    )(reminders),
    sendReminders:
      Array.isArray(reminders) &&
      !!reminders.filter(({ days, minutes }) => days || minutes).length
  }

  let i = 0
  while (updFrequency.reminders.length < 3) {
    updFrequency.reminders.push({
      days: 0,
      minutes: isClinicalPathway ? 0 : ++i * 60,
      isActive: reminders?.length > 0 ? false : !isClinicalPathway
    })
  }

  switch (type) {
    case FREQUENCY_TYPE_LIST.get(0).value:
      updFrequency.days = {
        ...updFrequency.days,
        days: [get(frequency, 'days.days', 1)]
      }
      break

    case FREQUENCY_TYPE_LIST.get(1).value:
      updFrequency.days = {
        ...updFrequency.days,
        weekDays: get(frequency, 'days.days', [])
      }
      break

    case FREQUENCY_TYPE_LIST.get(2).value:
      updFrequency.days = {
        ...updFrequency.days,
        monthDays: get(frequency, 'days.days', [])
      }
      break

    case FREQUENCY_TYPE_LIST.get(3).value:
      updFrequency.days = {
        ...updFrequency.days,
        occurrenceCount: get(frequency, 'days.days', []).length,
        occurrenceDays: get(frequency, 'days.days', [])
      }
      break

    case FREQUENCY_TYPE_LIST.get(4).value:
      updFrequency.days = {
        type: FREQUENCY_TYPE_LIST.get(4).value
      }
      break

    default:
      break
  }

  return updFrequency
}

export const prepareMeasurementRuleList = (rules = []) => {
  const isNoAnswerIndex = findKey(rules, ({ isNoAnswer }) => isNoAnswer)
  let updRules = cloneDeep(rules)

  if (!isEmpty(isNoAnswerIndex)) {
    updRules = moveItem(updRules, isNoAnswerIndex, updRules.length - 1)
  }

  return updRules.map((item, key) => ({
    ...item,
    key,
    escalate: !!item.alertPriority,
    patientAlertText: isArray(item.patientAlertText)
      ? {}
      : item.patientAlertText
  }))
}

export const prepareFrequencyRequest = (frequency = {}) => {
  const type = get(
    frequency,
    ['days', 'type'],
    FREQUENCY_TYPE_LIST.get(0).value
  )
  const muted = get(frequency, ['muted'], false)
  const every = +get(frequency, ['days', 'every'], 0)
  const sendReminders = get(frequency, ['sendReminders'], true)
  const reminders =
    sendReminders && isArray(frequency.reminders)
      ? get(frequency, ['reminders'], [])
          .filter(({ isActive }) => isActive)
          .map(({ days, minutes }) => ({
            days: +days,
            minutes: +minutes
          }))
      : null
  const deleteFieldList = ['days.occurrenceCount', 'sendReminders']
  const assignObj = {
    muted,
    days: {
      type,
      every
    },
    hours: {},
    reminders
  }

  switch (type) {
    case FREQUENCY_TYPE_LIST.get(0).value:
      assignObj.days = {
        ...assignObj.days,
        days: +get(frequency, ['days', 'every'], 0)
      }
      break

    case FREQUENCY_TYPE_LIST.get(1).value:
      assignObj.days = {
        ...assignObj.days,
        days: get(frequency, ['days', 'weekDays'], []).map((item) => +item)
      }
      break

    case FREQUENCY_TYPE_LIST.get(2).value:
      assignObj.days = {
        ...assignObj.days,
        days: get(frequency, ['days', 'monthDays'], []).map((item) => +item)
      }
      break

    case FREQUENCY_TYPE_LIST.get(3).value:
      {
        const occurrenceCount = get(frequency, ['days', 'occurrenceCount'], 0)

        assignObj.days = {
          ...assignObj.days,
          days: get(frequency, ['days', 'occurrenceDays'], [])
            .filter((__, key) => key < occurrenceCount)
            .map((item) => +item)
            .sort()
        }

        deleteFieldList.push('days.every')
      }
      break

    case FREQUENCY_TYPE_LIST.get(4).value:
      assignObj.days = {
        type: FREQUENCY_TYPE_LIST.get(4).value
      }
      break

    default:
      break
  }

  if (type !== FREQUENCY_TYPE_LIST.get(4).value) {
    assignObj.hours = get(frequency, 'hours', [])
      .filter((item) => !isEmpty(item))
      .map((item) => {
        return FREQUENCY_TYPE_LIST.get(4).value === 4
          ? []
          : moment.isMoment(item.time)
          ? item.time.seconds(0).format(TIME_FORMAT_FOR_SERVER)
          : moment(item.time, TIME_FORMAT_FOR_SERVER)
              .seconds(0)
              .format(TIME_FORMAT_FOR_SERVER)
      })
      .sort((a, b) =>
        moment(a, TIME_FORMAT_FOR_SERVER).diff(
          moment(b, TIME_FORMAT_FOR_SERVER)
        )
      )

    return flow(
      cloneDeep,
      (freq) => assign(freq, assignObj),
      (freq) => omit(freq, deleteFieldList)
    )(frequency)
  } else {
    assignObj.hours = []
    return assignObj
  }
}

export const buildTreeViaParent = ({
  itemList = [],
  parenField = null,
  parentValue = null
}) => {
  const result = []

  cloneDeep(itemList).forEach((item) => {
    if (get(item, parenField, null) === parentValue) {
      item.children = buildTreeViaParent({
        itemList,
        parenField,
        parentValue: item.uuid
      })

      if (!item.children.length) {
        delete item.children
      }

      result.push(item)
    }
  })

  return result
}

export const disableParentItem = (hierarchy = []) => {
  if (!hierarchy || !hierarchy.map) {
    return false
  }

  return hierarchy.map((node) => {
    const companyDivision = {
      title: node.name,
      value: node.uuid,
      key: node.uuid,
      children: disableParentItem(node.children)
    }

    if (Array.isArray(node.children) && node.children.length !== 0) {
      companyDivision.disabled = true
    }
    return companyDivision
  })
}

export const getCareTeamRelatedValues = (
  formCareTeams = [],
  careTeams = []
) => {
  const relatedValues = careTeams
    ?.filter(({ value: careTeamValue }) =>
      formCareTeams.includes(careTeamValue)
    )
    .reduce(
      (
        accumulator = {},
        {
          caseManager = {},
          clinician = {},
          division = {},
          group = {},
          provider = {}
        }
      ) => {
        if (caseManager?.uuid) {
          accumulator.caseManagers.push(caseManager.uuid)
        }

        if (clinician?.uuid) {
          accumulator.clinicians.push(clinician.uuid)
        }

        if (division?.uuid) {
          accumulator.divisions.push(division.uuid)
        }

        if (group?.uuid) {
          accumulator.groups.push(group.uuid)
        }

        if (provider?.uuid) {
          accumulator.providers.push(provider.uuid)
        }

        return accumulator
      },
      {
        caseManagers: [],
        clinicians: [],
        divisions: [],
        groups: [],
        providers: []
      }
    )

  return relatedValues
}

export const getSelectedCareTeams = (careTeams = [], selectedValues = []) => {
  return careTeams?.[0].children?.filter(({ value: careTeamValue }) =>
    selectedValues.includes(careTeamValue)
  )
}
